/*
 * Список Application ArgoCD по селектору сущности (аннотация argocd/app-selector).
 *
 * Повторяет логику карточки Roadie (useAppDetails): при argocd.appLocatorMethods инстансы
 * ищутся через service locator бэкенда (по одному инстансу на кластер, файл ведёт модуль
 * argocdInstances), иначе используется единственный argocd.baseUrl.
 */
import { Entity } from '@backstage/catalog-model';
import { ConfigApi, configApiRef, useApi } from '@backstage/core-plugin-api';
import {
  ARGOCD_ANNOTATION_APP_SELECTOR,
  argoCDApiRef,
} from '@roadiehq/backstage-plugin-argo-cd';
import useAsync from 'react-use/esm/useAsync';

export type ArgoApp = {
  name: string;
  namespace?: string;
  /** Имя инстанса ArgoCD — совпадает с именем кластера в argocd.appLocatorMethods. */
  instance?: string;
  /** Базовый URL инстанса: из него собирается ссылка на Application в UI ArgoCD. */
  instanceUrl?: string;
  health: string;
  sync: string;
};

type ArgoCDApi = typeof argoCDApiRef.T;

export const argoAppSelector = (entity: Entity): string | undefined =>
  entity.metadata.annotations?.[ARGOCD_ANNOTATION_APP_SELECTOR];

export async function fetchArgoApps(
  api: ArgoCDApi,
  config: ConfigApi,
  appSelector: string,
): Promise<ArgoApp[]> {
  const url = config.getOptionalString('argocd.baseUrl') ?? '';
  const useLocator = Boolean(
    config.getOptionalConfigArray('argocd.appLocatorMethods')?.length,
  );

  let items;
  if (useLocator) {
    const instances = await api.serviceLocatorUrl({ appSelector });
    if (instances instanceof Error) {
      throw instances;
    }
    const lists = await Promise.all(
      instances.map(instance =>
        api.getAppListDetails({ url, appSelector, instance: instance.name }),
      ),
    );
    items = lists.flatMap(list => list.items ?? []);
  } else {
    items = (await api.listApps({ url, appSelector })).items ?? [];
  }

  return items.map(item => ({
    name: item.metadata.name,
    namespace: item.metadata.namespace,
    instance: item.metadata.instance?.name,
    instanceUrl: item.metadata.instance?.url,
    health: item.status?.health?.status ?? 'Unknown',
    sync: item.status?.sync?.status ?? 'Unknown',
  }));
}

/** Приложения ArgoCD сущности; пустой список, если у неё нет селектора. */
export function useArgoApps(entity: Entity) {
  const api = useApi(argoCDApiRef);
  const config = useApi(configApiRef);
  const selector = argoAppSelector(entity);
  return useAsync(
    async () => (selector ? fetchArgoApps(api, config, selector) : []),
    [api, config, selector],
  );
}

export const appUrl = (app: ArgoApp): string | undefined =>
  app.instanceUrl
    ? `${app.instanceUrl.replace(/\/$/, '')}/applications/${app.name}`
    : undefined;

export const isHealthy = (app: ArgoApp): boolean => app.health === 'Healthy';
